import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core'
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms'
import {
  IComplexDatepickerModel,
  IShortcut,
  TRelativeMoment,
  TRelativeRef,
  TRelativeUnit,
} from './complex-datepicker.model'
import { datesComparator } from '../../validators/two-dates.validator'
import { atLeast } from '../../validators/at-least.validator'
import { conditional } from '../../validators/conditional.validator'
import { Subscription } from 'rxjs'
import { ComplexDatepickerService } from './complex-datepicker.service'
import { ComplexShortcutsEnum } from './complex-shortcuts.enum'

type DateTimeFormType = {
  datetime_start: FormControl<Date | string | null>
  datetime_end: FormControl<Date | string | null>
}

type RelativeTimeFormType = {
  relative_time_value: FormControl<number | null>
  relative_time_unit: FormControl<string | null>
  relative_time_moment: FormControl<string | null>
  relative_time_ref_date: FormControl<Date | string | null>
  relative_time_ref_now: FormControl<boolean | null>
}

@Component({
    selector: 'app-complex-datepicker',
    templateUrl: './complex-datepicker.component.html',
    styleUrls: ['./complex-datepicker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ComplexDatepickerComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class ComplexDatepickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() shortcuts: IShortcut[] = []
  @Input() display: { shortcut: boolean, datetime: boolean, relative: boolean, snapshot: boolean } = {
    shortcut: true,
    datetime: true,
    relative: true,
    snapshot: false
  }

  public relative_ref_date_subscriber: Subscription
  public isWidgetDisplayed: boolean = false
  public isCloseIconDisplayed: boolean = false
  public defaultTime: Date
  public isCollapsed: any = { shortcut: false, datetime: false, relative: false }

  private _selection: IComplexDatepickerModel
  private _relative_time_moments: TRelativeMoment[] = []
  private _relative_time_units: TRelativeUnit[] = []

  public get relative_time_moments(): TRelativeMoment[] {
    return this._relative_time_moments
  }

  public get relative_time_units(): TRelativeUnit[] {
    return this._relative_time_units
  }

  // Forms
  public datetime_form: FormGroup<DateTimeFormType>
  public relative_time_form: FormGroup<RelativeTimeFormType>
  // Date Time Controls
  public datetime_start: FormControl<Date | string | null>
  public datetime_end: FormControl<Date | string | null>
  // Relative Time Controls
  public relative_time_value: FormControl<number | null>
  public relative_time_unit: FormControl<string | null>
  public relative_time_moment: FormControl<string | null>
  public relative_time_ref_date: FormControl<Date | string | null>
  public relative_time_ref_now: FormControl<boolean | null>

  public disabled: boolean = false

  public onChange: any = (): void => {}

  public onTouched: any = (): void => {}

  public writeValue(obj: string | null): void {
    this._selection = JSON.parse(obj)
    this.prefillForms()
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  /** private setSelection(obj: string | null): void {
    if (obj) {
      const parsed = JSON.parse(obj)
      if (parsed.type === 'S' && parsed.shortcut) {
        for (let shortcut of this.shortcuts) {
          if (JSON.stringify(shortcut.relative) === JSON.stringify(parsed.shortcut.relative)) {
            this._selection = { type: 'S', shortcut: { relative: shortcut.relative } }
            break
          }
        }
      } else {
        this._selection = parsed
      }
    } else {
      this._selection = null
    }
  } */

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled
    if (this.disabled) {
      this.datetime_start.disable()
      this.datetime_end.disable()
      this.relative_time_unit.disable()
      this.relative_time_value.disable()
      this.relative_time_moment.disable()
      this.relative_time_ref_date.disable()
      this.relative_time_ref_now.disable()
    }
  }

  public constructor(private service: ComplexDatepickerService) {}

  public getDefaultTime(): Date {
    this.defaultTime = new Date()
    this.defaultTime.setHours(0, 0, 0)
    return this.defaultTime
  }

  public initControls(): void {
    this.initDateTimeControls()
    this.initRelativeTimeControls()
  }

  private initDateTimeControls(): void {
    this.datetime_start = new FormControl<Date | string | null>(null)
    this.datetime_end = new FormControl<Date | string | null>(null)
  }

  private initRelativeTimeControls(): void {
    this.relative_time_value = new FormControl<number | null>(null, [Validators.required])
    this.relative_time_unit = new FormControl<string | null>('hour', [Validators.required])
    this.relative_time_moment = new FormControl<string | null>('before', [Validators.required])
    this.relative_time_ref_date = new FormControl<Date | string | null>(null, [
      conditional(() => !this.relative_time_ref_now.value, Validators.required),
    ])
    this.relative_time_ref_now = new FormControl<boolean | null>(null)
  }

  public initForms(): void {
    this.initDateTimeForm()
    this.initRelativeTimeForm()
  }

  private initDateTimeForm(): void {
    this.datetime_form = new FormGroup<DateTimeFormType>(
      {
        datetime_start: this.datetime_start,
        datetime_end: this.datetime_end,
      },
      {
        validators: [
          atLeast(['datetime_start', 'datetime_end'], 1),
          datesComparator('datetime_start', 'datetime_end', '>', true),
        ],
      },
    )
  }

  private initRelativeTimeForm(): void {
    this.relative_time_form = new FormGroup<RelativeTimeFormType>({
      relative_time_unit: this.relative_time_unit,
      relative_time_value: this.relative_time_value,
      relative_time_moment: this.relative_time_moment,
      relative_time_ref_date: this.relative_time_ref_date,
      relative_time_ref_now: this.relative_time_ref_now,
    })
  }

  public onChangeCheckboxNow(e): void {
    const isChecked = e.target.checked
    if (isChecked) {
      this.relative_time_ref_date.setValue(null)
      this.relative_time_ref_date.setValidators([])
    } else {
      this.relative_time_ref_date.setValidators([Validators.required])
    }
    this.relative_time_ref_date.enable()
  }

  public onSelectShortcut(shortcut: IShortcut): void {
    if (this.disabled) {
      return
    }
    this._selection = {
      type: 'S',
      shortcut: { relative: shortcut.relative },
    }
    this.afterNewSelection()
  }

  private resetDateTimeForm(): void {
    this.datetime_form.reset()
    this.datetime_form.patchValue({
      datetime_start: null,
      datetime_end: null,
    })
  }

  private resetRelativeTimeForm(): void {
    this.relative_time_form.reset()
    this.relative_time_form.patchValue({
      relative_time_unit: 'hour',
      relative_time_value: null,
      relative_time_moment: 'before',
      relative_time_ref_now: null,
      relative_time_ref_date: null,
    })
  }

  public onSubmitDateTimeForm(): void {
    if (this.disabled) {
      return
    }
    this._selection = {
      type: 'D',
      dates: {
        start: this.datetime_start.value ? this.datetime_start.value : null,
        end: this.datetime_end.value ? this.datetime_end.value : null,
      },
    }
    this.afterNewSelection()
  }

  public onSubmitRelativeTime(): void {
    if (this.disabled) {
      return
    }
    this._selection = {
      type: 'R',
      relative: {
        unit: this.relative_time_unit.value as TRelativeUnit,
        value: this.relative_time_value.value,
        moment: this.relative_time_moment.value as TRelativeMoment,
        ref: this.relative_time_ref_now.value
          ? ComplexShortcutsEnum.NOW
          : (this.relative_time_ref_date.value as TRelativeRef),
      },
    }
    this.afterNewSelection()
  }

  public onOpenCloseWidget(): void {
    this.isWidgetDisplayed = !this.isWidgetDisplayed
  }

  public onSnapshotNow(): void {
    if (this.disabled) {
      return
    }
    this._selection = {
      type: 'SN',
      relative: {
        unit: 'sec',
        value: 1,
        moment: 'before',
        ref: ComplexShortcutsEnum.CURRENT_TIME,
      },
    }
    this.afterNewSelection()
  }

  private afterNewSelection(): void {
    this.onChange(JSON.stringify(this._selection))
    this.closeWidget()
    this.resetDateTimeForm()
    this.resetRelativeTimeForm()
    this.isCollapsed.datetime = true
    this.isCollapsed.relative = true
  }

  public isSnapshotSelected(): boolean {
    return this._selection && this._selection.type === 'SN'
  }

  public closeWidget(): void {
    setTimeout((): void => {
      this.isWidgetDisplayed = false
    }, 100)
  }

  public getButtonLabel(): string {
    if (this._selection) {
      switch (true) {
        case this._selection.type === 'S':
          return this.getSelectedShortcutLabel()
        case this._selection.type === 'D':
          return (
            (this._selection.dates.start ? this.formatDate(this._selection.dates.start) : 'until') +
            ' / ' +
            (this._selection.dates.end ? this.formatDate(this._selection.dates.end) : 'now')
          )
        case this._selection.type === 'R':
          return (
            this._selection.relative.value +
            ' ' +
            this.service.pluralize(this._selection.relative.value, this._selection.relative.unit) +
            ' ' +
            this._selection.relative.moment +
            ' ' +
            (this._selection.relative.ref === ComplexShortcutsEnum.NOW
              ? 'now'
              : this.formatDate(this._selection.relative.ref))
          )
        case this._selection.type === 'SN':
          return 'Snapshot NOW'
      }
    }
    return 'Please select'
  }

  private formatDate(date: any): string {
    const search: string = '/'
    return new Date(date)
      .toLocaleString('en-GB')
      .replace(', ', ' ')
      .replace(new RegExp(search, 'g'), '-')
  }

  private initShortcuts(): void {
    if (this.shortcuts.length === 0) {
      this.shortcuts = this.service.getDefaultShortcuts()
    }
  }

  private initRelativeTimeMoments(): void {
    this._relative_time_moments = this.service.getDefaultRelativeTimeMoments()
  }

  private initRelativeTimeUnits(): void {
    this._relative_time_units = this.service.getDefaultRelativeTimeUnits()
  }

  public isShortcutSelected(shortcut: IShortcut): boolean {
    return (
      this._selection
      && this._selection.type === 'S'
      && (
          this._selection.shortcut.label === shortcut.label
          || JSON.stringify(this._selection.shortcut.relative) === JSON.stringify(shortcut.relative)
      )
    )
  }

  private getSelectedShortcutLabel(): string {
    for (let shortcut of this.shortcuts) {
      if (this.isShortcutSelected(shortcut)) {
        return shortcut.label
      }
    }
    return 'Please select'
  }

  private prefillForms(): void {
    if (this._selection) {
      switch (true) {
        case this._selection.type === 'S':
          this.isCollapsed.shortcut = false
          this.isCollapsed.datetime = true
          this.isCollapsed.relative = true
          break
        case this._selection.type === 'D':
          this.datetime_form.patchValue({
            datetime_start: this._selection.dates.start
              ? this.getFormattedDate2Patch(this._selection.dates.start)
              : null,
            datetime_end: this._selection.dates.end
              ? this.getFormattedDate2Patch(this._selection.dates.end)
              : null,
          })
          this.isCollapsed.shortcut = true
          this.isCollapsed.datetime = false
          this.isCollapsed.relative = true
          break
        case this._selection.type === 'R':
          this.relative_time_form.patchValue({
            relative_time_unit: this._selection.relative.unit,
            relative_time_value: this._selection.relative.value,
            relative_time_moment: this._selection.relative.moment,
            relative_time_ref_now:
              this._selection.relative.ref === ComplexShortcutsEnum.NOW ? true : null,
            relative_time_ref_date:
              this._selection.relative.ref !== ComplexShortcutsEnum.NOW
                ? this.getFormattedDate2Patch(this._selection.relative.ref)
                : null,
          })
          if (this._selection.relative.ref === ComplexShortcutsEnum.NOW) {
            this.relative_time_ref_date.setValidators([])
            this.relative_time_ref_date.enable()
          }
          this.isCollapsed.shortcut = true
          this.isCollapsed.datetime = true
          this.isCollapsed.relative = false
          break
        case this._selection.type === 'SN':
          this.isCollapsed.shortcut = true
          this.isCollapsed.datetime = true
          this.isCollapsed.relative = true
          break
      }
    }
  }

  private getFormattedDate2Patch(date: Date | string | null): Date | null {
    if (typeof date === 'string') {
      return new Date(Date.parse(date))
    }
    return date
  }

  public clearWidget(): void {
    this.isCloseIconDisplayed = false
    this._selection = undefined
    this.resetDateTimeForm()
    this.resetRelativeTimeForm()
    this.onChange(null)
    this.closeWidget()
  }

  public onCalendarIconMouseOver(): void {
    if (!this._selection) {
      return
    }
    this.isCloseIconDisplayed = true
  }

  public onCalendarIconMouseOut(): void {
    if (!this._selection) {
      return
    }
    setTimeout((): void => {
      this.isCloseIconDisplayed = false
    }, 100)
  }

  private detectRelativeTimeFormChanges(): void {
    this.relative_ref_date_subscriber = this.relative_time_ref_date.valueChanges.subscribe(data => {
      if (data) {
        this.relative_time_ref_now.setValue(null)
      }
    })
  }

  ngOnInit(): void {
    this.initControls()
    this.initForms()
    this.initShortcuts()
    this.initRelativeTimeMoments()
    this.initRelativeTimeUnits()
    this.detectRelativeTimeFormChanges()
  }

  ngOnDestroy(): void {
    if (this.relative_ref_date_subscriber) {
      this.relative_ref_date_subscriber.unsubscribe()
    }
  }
}
